import TomSelect from 'tom-select'
import { ncs } from 'chromonym/ncs'
import { ral } from 'chromonym/ral'

import { createUnavailableLabel, getUnavailableValueIds, OPTION_COMBINATIONS_UPDATED_EVENT } from './option-combinations.js'
import { productColorFilter } from './product-color-filter.js'

const SELECTOR = 'select[data-searchable-select]'

export default class SearchableSelect {
  constructor() {
    this.handleContentLoaded = this.handleContentLoaded.bind(this)
    this.handleHtmxLoad = this.handleHtmxLoad.bind(this)
  }

  init() {
    this.enhance(document)

    document.addEventListener('remote-modal:content-loaded', this.handleContentLoaded)
    document.addEventListener('htmx:load', this.handleHtmxLoad)
  }

  handleContentLoaded(event) {
    if (!(event.target instanceof Element)) {
      return
    }

    this.enhance(event.target)
  }

  handleHtmxLoad(event) {
    if (!(event.detail?.elt instanceof Element)) {
      return
    }

    this.enhance(event.detail.elt)
  }

  enhance(root) {
    const elements = []

    if (root.matches?.(SELECTOR)) {
      elements.push(root)
    }

    root.querySelectorAll?.(SELECTOR).forEach((element) => elements.push(element))

    elements.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) {
        return
      }

      if (select.tomselect) {
        return
      }

      if (select.hasAttribute('data-typesense-product-color-select')) {
        this.enhanceProductColorSelect(select)

        return
      }

      const tomSelect = new TomSelect(select, {
        create: false,
        allowEmptyOption: false,
        closeAfterSelect: true,
        openOnFocus: true,
        selectOnTab: true,
        maxItems: 1,
        maxOptions: null,
        dropdownParent: 'body',
        render: {
          no_results: () => {
            const text = select.dataset.noResultsText || 'Geen resultaten gevonden'

            return `
        <div class="no-results">
          ${this.escapeHtml(text)}
        </div>
      `
          },
        },
      })

      if (select.classList.contains('is-invalid')) {
        tomSelect.wrapper.classList.add('is-invalid')
      }
    })
  }

  enhanceProductColorSelect(select) {
    const locale = window.polyestershoppen?.config?.locale
    const language = this.getLanguage(locale)
    const colorNameField = `color_name_${language}`
    const productNameField = `product_name_${language}`

    const colorNameFields = ['color_name_nl', 'color_name_en', 'color_name_fr']
    const productNameFields = ['product_name_nl', 'product_name_en', 'product_name_fr']

    const queryFields = [
      colorNameField,
      'search_terms',
      ...colorNameFields.filter((field) => field !== colorNameField),
      productNameField,
      ...productNameFields.filter((field) => field !== productNameField),
    ]

    const typesense = window.polyestershoppen?.typesense
    const collection = typesense?.collections?.productColor
    const productId = Number(select.dataset.productId)
    const perPage = 20
    const searchableSelect = this

    if (!typesense?.host || !typesense?.apiKey || !collection || !Number.isInteger(productId) || productId <= 0) {
      console.error('Incomplete Typesense product color configuration.')

      return
    }

    const origin = `${typesense.protocol}://${typesense.host}${typesense.port ? `:${typesense.port}` : ''}`
    const searchUrl = new URL('/multi_search', origin)
    const colorFilter = productColorFilter(select, productId)
    const form = select.closest('[data-product-configurator]')
    // Built from text nodes, so its HTML is already escaped
    const unavailableLabel = createUnavailableLabel(form, 'fs-xs text-body-secondary ms-auto').outerHTML

    const tomSelect = new TomSelect(select, {
      plugins: ['virtual_scroll'],
      create: false,
      allowEmptyOption: false,
      closeAfterSelect: true,
      openOnFocus: true,
      preload: true,
      selectOnTab: true,
      maxItems: 1,
      maxOptions: null,
      searchField: [],
      dropdownParent: 'body',

      firstUrl() {
        const pageUrl = new URL(searchUrl)

        pageUrl.searchParams.set('page', '1')

        return pageUrl.toString()
      },

      async load(query, callback) {
        const pageUrl = new URL(this.getUrl(query))
        const page = Number(pageUrl.searchParams.get('page')) || 1
        const normalizedQuery = searchableSelect.normalizeProductColorQuery(query)

        try {
          const response = await fetch(searchUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-TYPESENSE-API-KEY': typesense.apiKey,
            },
            body: JSON.stringify({
              searches: [
                {
                  collection,
                  q: normalizedQuery || '*',
                  query_by: queryFields.join(','),
                  filter_by: colorFilter,
                  page,
                  per_page: perPage,
                },
              ],
            }),
          })

          if (!response.ok) {
            throw new Error(`Typesense returned status ${response.status}.`)
          }

          const data = await response.json()
          const result = data.results?.[0]

          if (result?.error) {
            throw new Error(result.error)
          }

          // Colours that conflict with the choices in the other option groups are greyed out, not hidden
          const unavailable = getUnavailableValueIds(select)

          const options = (result?.hits ?? []).map(({ document }) => ({
            value: String(document.id),
            disabled: unavailable.has(String(document.id)),
            text: document[colorNameField] || document.color_name_nl || document.color_name_en || document.color_name_fr || String(document.id),
            hex: document.hex ?? null,
            color_system: document.color_system ?? null,
            color_name_nl: document.color_name_nl ?? '',
            color_name_en: document.color_name_en ?? '',
            color_name_fr: document.color_name_fr ?? '',
          }))

          const found = Number(result?.found ?? 0)
          const hasNextPage = page * perPage < found

          if (hasNextPage) {
            const nextPageUrl = new URL(searchUrl)

            nextPageUrl.searchParams.set('page', String(page + 1))
            this.setNextUrl(query, nextPageUrl.toString())
          }

          callback(options)
        } catch (error) {
          console.error('Typesense product color search failed.', error)
          callback()
        }
      },

      render: {
        option: (data, escape) => searchableSelect.renderProductColor(data, escape, data.disabled ? unavailableLabel : ''),
        item: (data, escape) => searchableSelect.renderProductColor(data, escape),

        no_results: () => {
          const text = select.dataset.noResultsText || 'Geen resultaten gevonden'

          return `
          <div class="no-results">
            ${searchableSelect.escapeHtml(text)}
          </div>
        `
        },
      },
    })

    if (select.classList.contains('is-invalid')) {
      tomSelect.wrapper.classList.add('is-invalid')
    }

    /*
     * Colours loaded earlier are greyed out again when another option group changes. The
     * virtual_scroll plugin restores its first page of colours as they were loaded when the dropdown
     * closes or the search is emptied, so they are updated again after that too.
     */
    const updateUnavailableColors = () => this.updateUnavailableColors(select)

    form?.addEventListener(OPTION_COMBINATIONS_UPDATED_EVENT, updateUnavailableColors)
    tomSelect.on('dropdown_close', updateUnavailableColors)
    tomSelect.on('type', (query) => {
      if (query === '') {
        updateUnavailableColors()
      }
    })
  }

  updateUnavailableColors(select) {
    const tomSelect = select.tomselect
    const unavailable = getUnavailableValueIds(select)

    Object.entries(tomSelect.options).forEach(([value, data]) => {
      const disabled = Boolean(value) && unavailable.has(value)

      if (Boolean(data.disabled) === disabled) {
        return
      }

      // Updated in place, so the copy that virtual_scroll keeps of the first page stays in sync
      data.disabled = disabled
      tomSelect.updateOption(value, data)
    })
  }

  renderProductColor(data, escape, unavailableLabel = '') {
    const hex = this.getProductColorPreviewHex(data)

    return `
    <div class="d-flex align-items-center gap-2">
      ${
        hex
          ? `<span
              class="d-inline-block flex-shrink-0 rounded-circle border"
              style="width: 1rem; height: 1rem; background-color: #${hex};"
            ></span>`
          : ''
      }
      <span>${escape(data.text)}</span>
      ${unavailableLabel}
    </div>
  `
  }

  normalizeHex(value) {
    const hex = String(value ?? '')
      .trim()
      .replace(/^#/, '')
      .toUpperCase()

    if (!/^[0-9A-F]{6}$/.test(hex)) {
      return null
    }

    return hex
  }

  getProductColorPreviewHex(data) {
    const existingHex = this.normalizeHex(data.hex)

    if (existingHex) {
      return existingHex
    }

    const names = [data.color_name_nl, data.color_name_en, data.color_name_fr].filter(Boolean)

    if (data.color_system === 'ncs') {
      for (const name of names) {
        const match = String(name).match(/\b(?:NCS[\s-]*)?(?:S[\s-]*)?(\d{4}-(?:N|[YRGB](?:\d{2}[YRGB])?))\b/i)

        if (!match) {
          continue
        }

        const hex = ncs.colors[match[1].toUpperCase()]

        if (hex) {
          return this.normalizeHex(hex)
        }
      }
    }

    if (data.color_system === 'ral') {
      for (const name of names) {
        const match = String(name).match(/\bRAL[\s-]*(\d{4})\b/i)

        if (!match) {
          continue
        }

        const hex = ral.colors[`RAL ${match[1]}`]

        if (hex) {
          return this.normalizeHex(hex)
        }
      }
    }

    return null
  }

  getLanguage(locale) {
    const language = String(locale ?? 'nl')
      .split(/[-_]/)[0]
      .toLowerCase()

    if (['nl', 'en', 'fr'].includes(language)) {
      return language
    }

    return 'nl'
  }

  normalizeProductColorQuery(query) {
    return String(query ?? '')
      .replace(/\bRAL[\s-]*(\d{4})\b/gi, 'RAL $1')
      .trim()
  }

  escapeHtml(value) {
    const element = document.createElement('div')

    element.textContent = value

    return element.innerHTML
  }
}
